import { useMutation, useQuery, useQueryClient, useTRPC } from "@luckymenu/api-client/react";
import type { Bill } from "@luckymenu/billing-core";
import { keepPreviousData } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import {
	type CustomerDraft,
	customerDraftOf,
	customerFromDraft,
	EMPTY_CUSTOMER,
} from "../billing/customer";
import {
	type BillDiscount,
	type BillDiscounts,
	discountsInput,
	exceedsCashierLimit,
	NO_DISCOUNTS,
	recalculate,
	staffDiscountOf,
} from "../billing/discounts";
import { type BillingError, billingProblemOf } from "../billing/problems";
import { useStaffCore } from "../provider";
import { useCurrentMembership } from "./use-current";
import { useCan } from "./use-navigation";

/**
 * Billing for both staff apps: the bill screen, payments, a bill's page and
 * its actions. See docs/architecture/api.md, Billing (staff). The server
 * computes every bill; the screens preview with billing-core.
 */

/** `staff.billing.getSettings`: GST, service charge, UPI ID, the cashier limit. Any role reads it. */
export function useBillingSettings() {
	const trpc = useTRPC();
	const query = useQuery(trpc.staff.billing.getSettings.queryOptions());
	return {
		status: query.data
			? ("ready" as const)
			: query.isError
				? ("error" as const)
				: ("loading" as const),
		settings: query.data ?? null,
		retry: () => void query.refetch(),
	};
}

/** Puts an issued or changed bill in the cache, and refreshes the board, the tables and the lists. */
export function useBillCache() {
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	return useCallback(
		(bill: Bill) => {
			queryClient.setQueryData(trpc.staff.billing.getBill.queryKey({ billId: bill.id }), bill);
			queryClient.setQueryData(
				trpc.staff.billing.getBill.queryKey({ orderId: bill.orderId }),
				bill,
			);
			void queryClient.invalidateQueries({ queryKey: trpc.staff.ordering.pathKey() });
			void queryClient.invalidateQueries({ queryKey: trpc.staff.billing.listBills.pathKey() });
			void queryClient.invalidateQueries({ queryKey: trpc.staff.billing.preview.pathKey() });
		},
		[trpc, queryClient],
	);
}

/**
 * The bill screen of an open order (OP-11, P4-T16): its lines, discounts
 * with a reason (BL-07) on the bill or on an item, the optional customer
 * (BL-11, P4-T24: name and phone from the order until changed, and a
 * GSTIN), the totals with the tax breakup, and Bill.
 *
 * `calculation` is recalculated on the device as discounts change (from the
 * server's preview without them); `finalize()` sends the total the screen
 * shows, and a bill that would come out different is refused
 * (`totalChanged`), the preview fetched again for staff to check. Returns
 * the issued bill, or null with `problem` set.
 *
 * `status`: `billed` when the order has a bill already (`billId`; open its
 * payment screen), `closed` when it was cancelled or paid, `notInPlan` when
 * the plan has no billing.
 */
export function useBillScreen(orderId: string, options: { initialDiscounts?: BillDiscounts } = {}) {
	const trpc = useTRPC();
	const role = useCurrentMembership().data?.role ?? null;
	const canDiscount = useCan("bill.discount");
	const allowed = useCan("bill.create");
	// Offers staff took off, and the bill offer they chose (P9-T10): the
	// server's preview applies them, so they are part of its input.
	const [removedOfferIds, setRemovedOfferIds] = useState<string[]>([]);
	const [billOfferId, setBillOfferId] = useState<string | null | undefined>(undefined);
	const preview = useQuery({
		...trpc.staff.billing.preview.queryOptions({
			orderId,
			removedOfferIds,
			...(billOfferId !== undefined ? { billOfferId } : {}),
		}),
		enabled: allowed,
		placeholderData: keepPreviousData,
	});
	const order = useQuery({
		...trpc.staff.ordering.getOrder.queryOptions({ orderId }),
		enabled: allowed,
	});
	const { settings } = useBillingSettings();
	const [customerDraft, setCustomerDraft] = useState<CustomerDraft | null>(null);
	const [discounts, setDiscounts] = useState<BillDiscounts>(
		() => options.initialDiscounts ?? NO_DISCOUNTS,
	);
	const [problem, setProblem] = useState<BillingError | null>(null);
	const cache = useBillCache();
	const mutation = useMutation(
		trpc.staff.billing.finalize.mutationOptions({ onSuccess: (bill) => cache(bill) }),
	);
	const print = useStaffCore().print;
	const printers = useQuery({
		...trpc.staff.printing.listPrinters.queryOptions(),
		enabled: allowed,
	});
	const billPrinter = printers.data?.printers.find((p) => p.id === printers.data?.billPrinterId);
	const printsBill = Boolean(billPrinter?.isActive);

	const calculation = useMemo(
		() => (preview.data ? recalculate(preview.data.calculation, discounts) : null),
		[preview.data, discounts],
	);
	const customer = customerDraft ?? (order.data ? customerDraftOf(order.data) : EMPTY_CUSTOMER);
	const customerCheck = customerFromDraft(customer);
	const overLimit =
		calculation !== null &&
		role !== null &&
		settings !== null &&
		exceedsCashierLimit(role, settings.cashierDiscountLimitBp, {
			subtotalPaise: calculation.totals.subtotalPaise,
			discountPaise: staffDiscountOf(calculation),
		});

	// A refetch that finds the order billed or closed wins over the preview it had.
	const loadProblem = preview.isError ? billingProblemOf(preview.error) : null;
	const status =
		loadProblem?.problem === "orderBilled"
			? ("billed" as const)
			: loadProblem?.problem === "orderClosed" || loadProblem?.problem === "notFound"
				? ("closed" as const)
				: preview.data
					? ("ready" as const)
					: loadProblem?.problem === "notInPlan"
						? ("notInPlan" as const)
						: loadProblem
							? ("error" as const)
							: ("loading" as const);

	const finalize = useCallback(async (): Promise<Bill | null> => {
		if (!calculation) return null;
		setProblem(null);
		if (!customerCheck.ok) return null;
		try {
			const bill = await mutation.mutateAsync({
				orderId,
				...discountsInput(
					discounts,
					calculation.lines.map((l) => l.id),
				),
				removedOfferIds,
				...(billOfferId !== undefined ? { billOfferId } : {}),
				// Untouched, the bill takes the order's name and phone.
				...(customerDraft ? { customer: customerCheck.customer } : {}),
				expectedTotalPaise: calculation.totals.totalPaise,
			});
			// "Print and close" (P5-T26): finalising made the bill's print job;
			// this device prints it at once when it reaches the bill printer.
			if (printsBill) void print.printOwn("bill", bill.id);
			return bill;
		} catch (error) {
			const found = billingProblemOf(error);
			setProblem(found);
			if (found.problem === "totalChanged") void preview.refetch();
			return null;
		}
	}, [
		calculation,
		preview,
		mutation,
		orderId,
		discounts,
		customerCheck,
		customerDraft,
		printsBill,
		print,
		removedOfferIds,
		billOfferId,
	]);

	return {
		status,
		/** For `billed`: the order's bill. */
		billId: loadProblem?.billId ?? null,
		retry: () => void preview.refetch(),
		/** Order number, table, type and document kind, from the server's preview. */
		preview: preview.data ?? null,
		/** The bill as it will be, with the discounts. */
		calculation,
		discounts,
		canDiscount,
		setBillDiscount: useCallback((discount: BillDiscount | null) => {
			setProblem(null);
			setDiscounts((d) => ({ ...d, bill: discount }));
		}, []),
		setItemDiscount: useCallback((orderItemId: string, discount: BillDiscount | null) => {
			setProblem(null);
			setDiscounts((d) => {
				const items = { ...d.items };
				if (discount) items[orderItemId] = discount;
				else delete items[orderItemId];
				return { ...d, items };
			});
		}, []),
		/** Offers on this bill (item offers by offer, then the bill offer), from the calculation. */
		appliedOffers: calculation?.offers ?? [],
		/** Bill offers and the coupon this bill can have, most off first: staff may choose one. */
		eligibleOffers: preview.data?.eligibleOffers ?? [],
		/** The chosen bill offer: undefined for the best one, null for none. */
		billOfferId,
		/** Takes an offer off this bill. A removed bill offer is not chosen again by itself. */
		removeOffer: useCallback((offerId: string) => {
			setProblem(null);
			setRemovedOfferIds((ids) => (ids.includes(offerId) ? ids : [...ids, offerId]));
			setBillOfferId((chosen) => (chosen === offerId ? undefined : chosen));
		}, []),
		/** Picks a bill offer from `eligibleOffers`, or null for none. */
		chooseBillOffer: useCallback((offerId: string | null) => {
			setProblem(null);
			setBillOfferId(offerId);
			if (offerId) setRemovedOfferIds((ids) => ids.filter((i) => i !== offerId));
		}, []),
		/** Puts every removed offer back and lets the best bill offer apply again. */
		restoreOffers: useCallback(() => {
			setRemovedOfferIds([]);
			setBillOfferId(undefined);
		}, []),
		removedOfferIds,
		/** A cashier's discounts are over the owner's limit: the server will refuse the bill. */
		overLimit,
		limitBp: settings?.cashierDiscountLimitBp ?? null,
		/** Name, phone and GSTIN for the bill; `customerErrors` by field (`billing:customer.problems.*`). */
		customer,
		customerErrors: customerCheck.ok ? {} : customerCheck.errors,
		setCustomer: useCallback(
			(field: keyof CustomerDraft, value: string) =>
				setCustomerDraft((d) => ({
					...(d ?? (order.data ? customerDraftOf(order.data) : EMPTY_CUSTOMER)),
					[field]: value,
				})),
			[order.data],
		),
		finalize,
		/** A bill printer is set: finalising prints the bill ("Print and close", P5-T26). */
		printsBill,
		busy: mutation.isPending,
		problem,
		clearProblem: useCallback(() => setProblem(null), []),
	};
}
